import { NextFunction, Request, Response } from "express";
import jwt from "jsonwebtoken";

// Extended Request interface for guest users
interface GuestRequest extends Request {
  guest?: {
    token: string;
    permissions: string[];
  };
}

/**
 * Middleware to authenticate guest users
 * Guest users have limited access to public school data
 */
export const authenticateGuest = (
  req: Request,
  res: Response,
  next: NextFunction,
): void => {
  try {
    const authHeader = req.headers.authorization;

    if (!authHeader?.startsWith("Bearer ")) {
      res.status(401).json({
        success: false,
        message: "Guest token required",
        error: "No guest token provided",
      });
      return;
    }

    const token = authHeader.slice(7); // Remove "Bearer " prefix

    // Verify guest token
    const decoded = jwt.verify(
      token,
      process.env.JWT_SECRET || "fallback-secret",
    ) as any;

    if (decoded.type !== "guest") {
      res.status(401).json({
        success: false,
        message: "Invalid guest token",
        error: "Token is not a guest token",
      });
      return;
    }

    // Attach guest context to request
    const guestReq = req as GuestRequest;
    guestReq.guest = {
      token,
      permissions: decoded.permissions || ["read:schools"],
    };

    next();
  } catch {
    res.status(401).json({
      success: false,
      message: "Invalid guest token",
      error: "Token verification failed",
    });
  }
};

/**
 * Middleware to check guest permissions
 * @param requiredPermissions - Array of required permissions
 */
export const requireGuestPermission = (requiredPermissions: string[]) => {
  return (req: Request, res: Response, next: NextFunction): void => {
    const guestReq = req as GuestRequest;
    const guestPermissions = guestReq.guest?.permissions || [];

    const hasPermission = requiredPermissions.every(permission =>
      guestPermissions.includes(permission),
    );

    if (!hasPermission) {
      res.status(403).json({
        success: false,
        message: "Insufficient permissions",
        error: `Required permissions: ${requiredPermissions.join(", ")}`,
      });
      return;
    }

    next();
  };
};

/**
 * Generate a guest token
 * This would typically be called by an admin or system
 */
export const generateGuestToken = (
  permissions: string[] = ["read:schools"],
): string => {
  const payload = {
    type: "guest",
    permissions,
    iat: Math.floor(Date.now() / 1000),
    exp: Math.floor(Date.now() / 1000) + 24 * 60 * 60, // 24 hours
  };

  return jwt.sign(payload, process.env.JWT_SECRET || "fallback-secret");
};
